import React, { useEffect, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart, 
  Pie, 
  Cell
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  MousePointerClick, 
  Download, 
  Calendar, 
  Filter, 
  ChevronDown, 
  X, 
  Search,
  MessageSquare,
  Phone,
  ExternalLink,
  Edit3,
  CheckCircle,
  Clock,
  UserMinus,
  MoreVertical,
  LayoutDashboard,
  UserCheck,
  Globe,
  Target
} from 'lucide-react';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { useToast } from './Toast';

const COLORS = ['#6366f1', '#a855f7', '#ec4899', '#f43f5e', '#f59e0b'];

export const ReportsView: React.FC = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'leads'>('leads');
  const [pages, setPages] = useState<any[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const exportToPDF = async () => {
    const element = document.getElementById('reports-content');
    if (!element) return;

    setIsExporting(true);
    showToast('Gerando seu relatório PDF...', 'info');
    
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#000000',
        logging: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`optivelo-crm-${activeTab}-${new Date().toISOString().split('T')[0]}.pdf`);
      showToast('Relatório exportado com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      showToast('Erro ao gerar PDF. Tente novamente.', 'error');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Filters State
  const [filterLeadStatus, setFilterLeadStatus] = useState<string>('all');
  const [filterPageId, setFilterPageId] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!auth.currentUser) return;

    // Listen to Pages
    const qPages = query(
      collection(db, 'landing_pages'),
      where('userId', '==', auth.currentUser.uid)
    );

    const unsubPages = onSnapshot(qPages, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPages(data);
    });

    // Listen to Leads
    const qLeads = query(
      collection(db, 'leads'),
      where('userId', '==', auth.currentUser.uid)
    );

    const unsubLeads = onSnapshot(qLeads, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((a: any, b: any) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : 0;
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : 0;
        return dateB - dateA;
      });
      setLeads(data);
      setLoading(false);
    });

    return () => {
      unsubPages();
      unsubLeads();
    };
  }, []);

  const updateLeadStatus = async (leadId: string, status: string) => {
    try {
      await updateDoc(doc(db, 'leads', leadId), { status });
      showToast('Status do lead atualizado!', 'success');
    } catch (err) {
      showToast('Erro ao atualizar status.', 'error');
    }
  };

  const deleteLead = async (leadId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este lead?')) return;
    try {
      await deleteDoc(doc(db, 'leads', leadId));
      showToast('Lead excluído.', 'success');
    } catch (err) {
      showToast('Erro ao excluir lead.', 'error');
    }
  };

  const openWhatsApp = (phone: string, name: string) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const message = encodeURIComponent(`Olá ${name}, vi seu interesse na nossa página e gostaria de conversar!`);
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  // Filtering Logic
  const filteredPages = pages.filter(page => {
    if (filterPageId !== 'all' && page.id !== filterPageId) return false;
    return true;
  });

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
      lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone?.includes(searchQuery);
    
    const matchesPage = filterPageId === 'all' || lead.pageId === filterPageId;
    const matchesStatus = filterLeadStatus === 'all' || lead.status === filterLeadStatus;
    
    // Date Filtering
    let matchesDate = true;
    if (lead.createdAt?.toDate) {
      const leadDate = lead.createdAt.toDate();
      leadDate.setHours(0, 0, 0, 0);
      
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (leadDate < start) matchesDate = false;
      }
      
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (leadDate > end) matchesDate = false;
      }
    }
    
    return matchesSearch && matchesPage && matchesStatus && matchesDate;
  });

  const totalTraffic = filteredPages.reduce((acc, p) => acc + (p.traffic || 0), 0);
  const totalConversions = leads.length;
  const avgCR = totalTraffic > 0 ? (totalConversions / totalTraffic) * 100 : 0;

  const pieData = filteredPages.map(p => ({
    name: p.name,
    value: p.traffic || 0
  })).filter(p => p.value > 0);

  const sourceData = leads.reduce((acc: any[], lead) => {
    const source = lead.source || 'organic';
    const label = source === 'paid' ? 'Pago' : source === 'email' ? 'E-mail' : source === 'direct' ? 'Direto' : 'Orgânico';
    const existing = acc.find(item => item.name === label);
    if (existing) existing.value++;
    else acc.push({ name: label, value: 1 });
    return acc;
  }, []);

  return (
    <div id="reports-content" className="p-8 space-y-8 animate-fade-in pb-32 bg-black">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-[12px] font-black uppercase tracking-[0.2em]">
              <div className="w-1 h-1 rounded-full bg-indigo-400 animate-pulse" />
              Centro de Comando Quantum
            </div>
            <div className="px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-500 text-[12px] font-bold uppercase tracking-widest">
              Sincronização Neural Ativa
            </div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-black text-white tracking-tighter uppercase">
            {activeTab === 'dashboard' ? 'Análise de Performance' : 'Inteligência de Leads (CRM)'}
          </h1>
          <p className="text-sm text-slate-400 font-medium">
            {activeTab === 'dashboard' 
              ? 'Processamento de dados em tempo real para escala global.' 
              : 'Gestão estratégica de contatos e funil de vendas.'}
          </p>
        </div>
        
        <div className="flex items-center gap-2 p-1 bg-white/5 border border-white/10 rounded-2xl">
          <button 
            onClick={() => setActiveTab('leads')}
            className={`px-6 py-2.5 rounded-xl text-[12px] font-bold uppercase tracking-widest transition-all flex items-center gap-2 ${
              activeTab === 'leads' ? 'bg-indigo-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'
            }`}
          >
            <UserCheck className="w-4 h-4" /> Leads
          </button>
          <button 
            onClick={() => setActiveTab('dashboard')}
            className={`px-6 py-2.5 rounded-xl text-[12px] font-bold uppercase tracking-widest transition-all flex items-center gap-2 ${
              activeTab === 'dashboard' ? 'bg-indigo-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'
            }`}
          >
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </button>
          <button 
            onClick={exportToPDF}
            disabled={isExporting}
            className="px-6 py-2.5 bg-white text-black rounded-xl text-[12px] font-bold uppercase tracking-widest hover:bg-slate-200 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            <Download className={`w-4 h-4 ${isExporting ? 'animate-spin' : ''}`} />
            {isExporting ? 'Exportando...' : 'Exportar PDF'}
          </button>
        </div>
      </div>

      {activeTab === 'dashboard' ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ReportStat title="Tráfego Total" value={totalTraffic.toLocaleString()} sub="Visitantes únicos" />
            <ReportStat title="Total de Leads" value={totalConversions.toLocaleString()} sub="Capturados no OptiVelo" />
            <ReportStat title="Taxa Média" value={`${avgCR.toFixed(2)}%`} sub="Conversão global" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="premium-card p-8 space-y-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-400" />
                Conversões por Canal
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sourceData.length > 0 ? sourceData : [{name: 'Sem dados', value: 1}]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {sourceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#161b22', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                      itemStyle={{ color: '#f0f6fc', fontSize: '11px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="premium-card p-8 space-y-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-400" />
                Performance por Página
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filteredPages.map(p => ({ name: p.name.substring(0, 10), cr: p.traffic > 0 ? (p.conversions / p.traffic) * 100 : 0 }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" vertical={false} />
                    <XAxis dataKey="name" stroke="#475569" fontSize={10} />
                    <YAxis stroke="#475569" fontSize={10} unit="%" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#161b22', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                      itemStyle={{ color: '#f0f6fc', fontSize: '11px' }}
                    />
                    <Bar dataKey="cr" fill="#a855f7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          {/* Leads Toolbar */}
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input 
                  type="text"
                  placeholder="Buscar por nome, e-mail ou telefone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-4 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all"
                />
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-6 py-4 rounded-2xl text-[12px] font-bold uppercase tracking-widest transition-all flex items-center gap-2 ${
                    showFilters ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  <Filter className="w-4 h-4" /> 
                  {showFilters ? 'Esconder Filtros' : 'Filtros Avançados'}
                </button>
                <button 
                  onClick={() => showToast('Exportando leads para CSV...', 'info')}
                  className="px-6 py-4 bg-white text-black text-[12px] font-bold uppercase tracking-widest rounded-2xl hover:bg-slate-200 transition-all flex items-center gap-2"
                >
                  <Download className="w-4 h-4" /> Exportar CSV
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-6 bg-white/[0.02] border border-white/5 rounded-3xl animate-in slide-in-from-top-2 duration-300">
                <div className="space-y-2">
                  <label className="text-[12px] font-bold text-slate-500 uppercase tracking-widest px-1">Status</label>
                  <select 
                    value={filterLeadStatus}
                    onChange={(e) => setFilterLeadStatus(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all"
                  >
                    <option value="all">Todos os Status</option>
                    <option value="new">Novo Lead</option>
                    <option value="contacted">Em Contato</option>
                    <option value="qualified">Qualificado</option>
                    <option value="closed">Venda Fechada</option>
                    <option value="lost">Perdido</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[12px] font-bold text-slate-500 uppercase tracking-widest px-1">Página de Origem</label>
                  <select 
                    value={filterPageId}
                    onChange={(e) => setFilterPageId(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all"
                  >
                    <option value="all">Todas as Páginas</option>
                    {pages.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[12px] font-bold text-slate-500 uppercase tracking-widest px-1">Data Inicial</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input 
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all color-scheme-dark"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[12px] font-bold text-slate-500 uppercase tracking-widest px-1">Data Final</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input 
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all color-scheme-dark"
                    />
                  </div>
                </div>

                <div className="md:col-span-4 flex justify-end">
                  <button 
                    onClick={() => {
                      setFilterLeadStatus('all');
                      setFilterPageId('all');
                      setStartDate('');
                      setEndDate('');
                      setSearchQuery('');
                    }}
                    className="text-[12px] font-bold text-slate-500 hover:text-white uppercase tracking-widest transition-all"
                  >
                    Limpar Filtros
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Leads List */}
          <div className="premium-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/5 bg-white/[0.02]">
                    <th className="px-6 py-4 text-[12px] font-bold text-slate-500 uppercase tracking-widest">Lead</th>
                    <th className="px-6 py-4 text-[12px] font-bold text-slate-500 uppercase tracking-widest">Origem</th>
                    <th className="px-6 py-4 text-[12px] font-bold text-slate-500 uppercase tracking-widest">Data</th>
                    <th className="px-6 py-4 text-[12px] font-bold text-slate-500 uppercase tracking-widest">Status</th>
                    <th className="px-6 py-4 text-[12px] font-bold text-slate-500 uppercase tracking-widest text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredLeads.length > 0 ? filteredLeads.map((lead) => (
                    <tr key={lead.id} className="group hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-bold border border-indigo-500/20">
                            {lead.name?.charAt(0) || lead.email?.charAt(0)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-bold text-white">{lead.name || 'Sem nome'}</p>
                              <span className="px-1.5 py-0.5 bg-indigo-500/10 border border-indigo-500/20 rounded text-[12px] font-bold text-indigo-400 uppercase tracking-widest">Elite Lead</span>
                            </div>
                            <p className="text-sm text-slate-500">{lead.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-6">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                          <span className="text-sm text-slate-300">{lead.pageName}</span>
                        </div>
                      </td>
                      <td className="px-6 py-6">
                        <span className="text-sm text-slate-500">
                          {lead.createdAt?.toDate ? lead.createdAt.toDate().toLocaleDateString('pt-BR') : 'Recente'}
                        </span>
                      </td>
                      <td className="px-6 py-6">
                        <div className="relative group/select">
                          <select 
                            value={lead.status || 'new'}
                            onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                            className={`text-[12px] font-bold uppercase tracking-widest px-4 py-2 rounded-xl border focus:outline-none transition-all cursor-pointer appearance-none pr-8 ${
                              lead.status === 'closed' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' :
                              lead.status === 'contacted' ? 'bg-blue-500/10 border-blue-500/20 text-blue-500' :
                              lead.status === 'lost' ? 'bg-rose-500/10 border-rose-500/20 text-rose-500' :
                              'bg-indigo-500/10 border-indigo-500/20 text-indigo-400'
                            }`}
                          >
                            <option value="new">Novo Lead</option>
                            <option value="contacted">Em Contato</option>
                            <option value="qualified">Qualificado</option>
                            <option value="closed">Venda Fechada</option>
                            <option value="lost">Perdido</option>
                          </select>
                          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 opacity-50 pointer-events-none" />
                        </div>
                      </td>
                      <td className="px-6 py-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {lead.phone && (
                            <button 
                              onClick={() => openWhatsApp(lead.phone, lead.name)}
                              className="p-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-all"
                              title="Chamar no WhatsApp"
                            >
                              <Phone className="w-4 h-4" />
                            </button>
                          )}
                          <button 
                            onClick={() => deleteLead(lead.id)}
                            className="p-2 bg-rose-500/10 text-rose-500 rounded-lg hover:bg-rose-500/20 transition-all"
                            title="Excluir Lead"
                          >
                            <UserMinus className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-20 text-center">
                        <div className="flex flex-col items-center gap-4 opacity-30">
                          <Users className="w-12 h-12" />
                          <p className="text-sm font-medium">Nenhum lead encontrado.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReportStat = ({ title, value, sub }: { title: string, value: string, sub: string }) => (
  <div className="premium-card p-6 space-y-2">
    <p className="micro-label">{title}</p>
    <h3 className="text-3xl font-bold text-white">{value}</h3>
    <p className="text-[12px] text-slate-500 font-medium uppercase tracking-widest">{sub}</p>
  </div>
);
